import {
	isSchemaActionRef,
	isSchemaConstAny,
	isSchemaConstArray,
	isSchemaConstBoolean,
	isSchemaConstEnum,
	isSchemaConstFloat,
	isSchemaConstInteger,
	isSchemaConstMap,
	isSchemaConstObject,
	isSchemaConstPassword,
	isSchemaConstSecret,
	isSchemaConstString,
	isSchemaExpression,
	isSchemaIconName,
	isSchemaIntegrationRef,
	isSchemaOneOf,
	isSchemaOptGroup,
	isSchemaRecursive,
	isSchemaResponsiveValue,
	isSchemaRouteRef,
	isSchemaStringWithConst,
	isSchemaValue,
	isSchemaViewRef,
	TGenericBlueprintSchema
} from "@adapptio/lib-blueprint";
import { isObject } from "@adapptio/lib-shared";
import { controlHiddenProp } from "../helpers/controlHiddenProp";
import { generateExampleType } from "../helpers/generateExampleTypes";

export const mapperExamples = (schema: TGenericBlueprintSchema, padding = "") => {
	if (isSchemaActionRef(schema)) {
		return (
			"{\n" +
			padding +
			"\t" +
			'actionId: "actionId",\n' +
			padding +
			"\t" +
			"params: {\n" +
			padding +
			"\t\t" +
			'"paramName": "value"\n' +
			padding +
			"\t" +
			"}\n" +
			padding +
			"}"
		);
	}

	if (isSchemaRouteRef(schema)) {
		return (
			"{\n" +
			padding +
			"\t" +
			'name: "routeName",\n' +
			padding +
			"\t" +
			"params: {\n" +
			padding +
			"\t\t" +
			'"routeParam": "param1"\n' +
			padding +
			"\t" +
			"}\n" +
			padding +
			"}"
		);
	}

	if (isSchemaIntegrationRef(schema)) {
		return (
			"{\n" +
			padding +
			"\t" +
			'integrationType: "restApi",\n' +
			padding +
			"\t" +
			'functionName: "request",\n' +
			padding +
			"\t" +
			'integrationId: "integrationId",\n' +
			padding +
			"\t" +
			"params: {\n" +
			padding +
			"\t\t" +
			'"method": "POST",\n' +
			padding +
			"\t\t" +
			'"query": {}\n' +
			padding +
			"\t" +
			"}\n" +
			padding +
			"}"
		);
	}

	if (isSchemaRecursive(schema)) {
		return mapperExamples(schema.opts.value(schema), padding);
	}

	if (isSchemaViewRef(schema)) {
		return (
			"{\n" +
			padding +
			"\t" +
			'viewId: "viewId",\n' +
			padding +
			"\t" +
			"params: {\n" +
			padding +
			"\t\t" +
			'"paramName": "value"\n' +
			padding +
			"\t" +
			"}\n" +
			padding +
			"}"
		);
	}

	if (isSchemaConstMap(schema)) {
		return (
			"{\n" +
			padding +
			"\t" +
			'"key": ' +
			mapperExamples(schema.opts.value, padding) +
			"\n" +
			padding +
			"}"
		);
	}

	if (isSchemaValue(schema) && !controlHiddenProp(schema)) {
		return mapperExamples(schema.constSchema, padding);
	}

	if (isSchemaResponsiveValue(schema)) {
		const opts = schema.opts;
		if (opts.default) {
			if (isObject(opts.default)) {
				const childProps = Object.keys(opts.default);

				return (
					"{" +
					(childProps.length > 0 ? "\n" : "") +
					childProps.map((prop) => padding + "\t" + prop + ": " + opts.default[prop]).join(",\n") +
					(childProps.length > 0 ? "\n" : "") +
					padding +
					"}"
				);
			}
			return `${opts.default}`;
		}
		return mapperExamples(schema.opts.value, padding + "\t");
	}

	// Mapper schema OneOf
	if (isSchemaOneOf(schema)) {
		const child = schema.opts.types;
		const keys = [],
			props = [];

		Object.keys(child).map((key) => {
			keys.push(`"${key}"`);
			props.push(key);
		});

		const bodyObj = props
			.map((prop) => {
				if (!controlHiddenProp(child[prop].value)) {
					return (
						padding + "\t\t" + prop + ": " + mapperExamples(child[prop].value, padding + "\t\t")
					);
				}
			})
			.filter((el) => el !== undefined);

		const stingObj =
			"{" +
			(props.length > 0 ? "\n" : "") +
			bodyObj.join(",\n") +
			(props.length > 0 ? "\n" : "") +
			padding +
			"\t}";

		return (
			"{\n" +
			padding +
			"\t" +
			"type: " +
			keys[0] +
			",\n" +
			padding +
			"\t" +
			"value: " +
			stingObj +
			"\n" +
			padding +
			"}"
		);
	}

	// Mapper schema Array
	if (isSchemaConstArray(schema)) {
		return (
			"[\n" + padding + "\t" + mapperExamples(schema.opts.items, padding + "\t") + "\n" + padding + "]"
		);
	}

	// Mapper schema Object
	if (isSchemaConstObject(schema)) {
		const child = schema.opts.props;
		const childProps = Object.keys(child);

		const bodyObj = childProps
			.map((prop) => {
				if (!controlHiddenProp(child[prop].schema)) {
					if (prop === "items") {
						return padding + "\t" + "child: self";
					} else {
						return (
							padding + "\t" + prop + ": " + mapperExamples(child[prop].schema, padding + "\t")
						);
					}
				}
			})
			.filter((el) => el !== undefined);

		return (
			"{" +
			(childProps.length > 0 ? "\n" : "") +
			bodyObj.join(",\n") +
			(childProps.length > 0 ? "\n" : "") +
			padding +
			"}"
		);
	}

	if (isSchemaOptGroup(schema)) {
		return mapperExamples(schema.opts["value"], padding);
	}

	if (isSchemaExpression(schema)) {
		if (schema.opts?.default) {
			return `${schema.opts?.default}`;
		}
		return `null`;
	}

	if (isSchemaConstPassword(schema) || isSchemaConstSecret(schema) || isSchemaConstString(schema)) {
		if (schema.opts["default"]?.length) {
			return `"${schema.opts["default"]}"`;
		} else if (schema.opts?.fallbackValue?.length) {
			return `"${schema.opts.fallbackValue}"`;
		}
		return generateExampleType("string");
	}

	if (isSchemaStringWithConst(schema)) {
		if (schema.opts.default?.length) {
			return `"${schema.opts.default}"`;
		} else if (schema.opts?.fallbackValue?.length) {
			return `"${schema.opts.fallbackValue}"`;
		}
		return `"${schema.opts.constants[0].value}"`;
	}

	if (isSchemaConstAny(schema)) {
		const opts = schema.opts;
		if (opts.defaultType) {
			return `${generateExampleType(opts.defaultType)}`;
		} else if (opts.allowedTypes) {
			const valueArr = Object.keys(opts.allowedTypes).map((key) => key);
			return `${generateExampleType(valueArr[0])}`;
		} else {
			return generateExampleType("any");
		}
	}

	if (isSchemaIconName(schema)) {
		if (schema.opts.default?.length) {
			return `${schema.opts.default}`;
		} else if (schema.opts?.fallbackValue?.length) {
			return `${schema.opts.fallbackValue}`;
		}
		return `"mdi/account"`;
	}

	if (isSchemaConstFloat(schema)) {
		if (schema.opts.default) {
			return `"${schema.opts.default}"`;
		} else if (schema.opts?.fallbackValue) {
			return `"${schema.opts.fallbackValue}"`;
		}
		return generateExampleType("Float");
	}

	if (isSchemaConstEnum(schema)) {
		const valueArr = schema.opts.options.map((item) => item.value);
		if (schema.opts["default"]) {
			return `"${schema.opts["default"]}"`;
		} else if (schema.opts.fallbackValue) {
			return `"${schema.opts.fallbackValue}"`;
		}
		return `${generateExampleType(valueArr[0])}`;
	}

	if (isSchemaConstBoolean(schema)) {
		if (schema.opts.default) {
			return `${schema.opts.default}`;
		} else if (schema.opts.fallbackValue) {
			return `${schema.opts.fallbackValue}`;
		}
		return `${generateExampleType("boolean")}`;
	}

	if (isSchemaConstInteger(schema)) {
		if (schema.opts.default) {
			return `${schema.opts.default}`;
		} else if (schema.opts.fallbackValue) {
			return `${schema.opts.fallbackValue}`;
		}
		return `${generateExampleType("Integer")}`;
	}
};
